import fs from 'fs'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import path from 'path'
import React, { useEffect, useState } from 'react'
import { track } from 'track'
import { AddOptionForm } from '@/components/AddOptionForm'
import { Option } from '@/components/Option'
import { PageTitle } from '@/components/PageTitle'
import { Panel } from '@/components/Panel'
import i18nextConfig from '@/next-i18next.config'
import { config } from '@/site.config.js'
import { useAppDispatch, useAppSelector } from '@/state/hooks'
import { getListData, sharedLists } from '@/state/lists'
import type { SharedListData, SharedListLocale } from '@/state/lists/types'
import {
  replaceMetaState,
  replaceOptionsState,
  replaceReasonsState,
  setAiDisabled,
} from '@/state/reducers'
import type { RootState } from '@/state/store'
import { isAiDisabled as checkAiDisabled } from '@/utils/aiFeatureStatus'
import { getI18nProps } from '@/utils/getStatic'
import languageDetector from '@/utils/languageDetector'

interface ListPageProps {
  listData: SharedListData
}

function ListPage({ listData }: ListPageProps) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const cachedLocale = localStorage.getItem('i18nextLng')
    if (
      router.query.locale !== cachedLocale &&
      languageDetector.cache &&
      router.query.locale
    ) {
      languageDetector.cache(router.query.locale.toString())
    }
  }, [router])

  useEffect(() => {
    dispatch(replaceOptionsState(listData.options))
    dispatch(replaceReasonsState(listData.reasons))
    dispatch(replaceMetaState({ fileName: listData.title, isSharedList: true }))
    dispatch(setAiDisabled(checkAiDisabled()))
    setMounted(true)
    track('event', 'view-shared-list', { slug: listData.slug })
  }, [dispatch, listData])

  const reduxOptions = useAppSelector((state: RootState) => state.options)
  const metaKey = useAppSelector((state: RootState) => state.meta.key)
  // Before mount, use props to match server render and avoid hydration mismatch.
  // After mount, Redux has been populated with the same data so the switch is seamless.
  const displayOptions = mounted ? reduxOptions : listData.options
  const pageTitle = t('SharedList.title', { listTitle: listData.title })
  const emphasisClass = ['ja', 'ko'].includes(router.query.locale as string)
    ? ''
    : 'italic'

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <Head>
        <meta name="description" content={listData.description} />
        <meta property="og:description" content={listData.description} />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:url"
          content={`${config.prodDomain}${router.asPath}`}
        />
        <meta property="og:site_name" content={t('title')} />
        <meta property="og:type" content="website" />
      </Head>

      <div className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-7">
          {!metaKey && (
            <div className="mb-6">
              <h1 className="font-black text-[30px] tracking-[-0.03em] text-ink dark:text-dark-ink leading-[1.15] mb-2">
                {listData.title}
              </h1>
              <p className="text-sm font-semibold text-ink-secondary dark:text-dark-ink2 mb-3">
                {listData.description}
              </p>
              <p
                data-test="shared-badge"
                className={`text-xs text-ink-secondary dark:text-dark-ink2 text-center ${emphasisClass}`}
              >
                {t('SharedList.cta')}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-5 [&>*:nth-child(odd)]:rotate-[-0.3deg] [&>*:nth-child(even)]:rotate-[0.3deg]">
            {displayOptions.map((option) => {
              const seedReasons = mounted
                ? undefined
                : listData.reasons.filter((r) => r.optionId === option.id)
              return (
                <Panel key={option.id}>
                  <Option option={option} seedReasons={seedReasons} />
                </Panel>
              )
            })}
          </div>

          <div className="mt-5">
            <AddOptionForm hasExistingOptions={true} />
          </div>

          <p
            className={`mt-6 text-xs text-ink-secondary dark:text-dark-ink2 text-center ${emphasisClass}`}
          >
            {t('SharedList.disclaimer')}
          </p>
        </div>
      </div>
    </>
  )
}

export default ListPage

function loadListLocale(slug: string, locale: string): SharedListLocale | null {
  const localesDir = path.join(process.cwd(), 'public', 'locales')
  const localePath = path.join(localesDir, locale, `list-${slug}.json`)
  const fallbackPath = path.join(localesDir, 'en', `list-${slug}.json`)
  const filePath = fs.existsSync(localePath)
    ? localePath
    : fs.existsSync(fallbackPath)
      ? fallbackPath
      : null
  if (!filePath) return null
  try {
    return JSON.parse(fs.readFileSync(filePath, 'utf-8')) as SharedListLocale
  } catch {
    return null
  }
}

export async function getStaticProps({
  params,
}: {
  params: { locale: string; slug: string }
}) {
  const { locale, slug } = params
  let listData: SharedListData
  try {
    listData = await getListData(slug)
  } catch {
    return { notFound: true }
  }

  const translations = loadListLocale(slug, locale)
  if (translations) {
    listData = {
      ...listData,
      title: translations.title,
      description: translations.description,
      options: listData.options.map((opt) => ({
        ...opt,
        label: translations.options[opt.id] ?? opt.label,
      })),
      reasons: listData.reasons.map((r) => ({
        ...r,
        label: translations.reasons[r.id] ?? r.label,
      })),
    }
  }

  return {
    props: {
      ...(await getI18nProps({ params }, [`list-${slug}`])),
      listData,
    },
  }
}

export function getStaticPaths() {
  const paths = i18nextConfig.i18n.locales.flatMap((locale) =>
    sharedLists.map(({ slug }) => ({
      params: { locale, slug },
    })),
  )
  return { paths, fallback: false }
}
